import * as React from "react";
import {createContext, useContext, useEffect, useReducer, useState} from "react";
import {
    getFieldValidationMeta,
    getFunctionSelectItemsByDealIdAndFieldNameAndFormName,
    updateFieldByRequest,
    validateField
} from "../api/validationApiClient";
import {IParametrizedFieldContext} from "./ParametrizedFieldContext";
import {FIELD_TYPE, IReactField, IResponseField} from "../model/reactField";
import {ISelectItem} from "../model/selectItem";
import {FieldRequest} from "../../src/api/model/validation";
import moment from "moment";
import {ITravelQuotationContext, TravelQuotationContext} from "../contexts/TravelQuotationContext";
import * as _ from 'lodash';
import {ErrorFieldsContext} from "./ErrorFieldsContext";
import {ProductType} from "../api/model/common";
import {ProcessingContext} from "./ProcessingContext";

const defaultContext: IParametrizedFieldContext  = {
    fields: new Array<any>(),
    registeredFields: new Array<IResponseField>(),
    getFieldByName: () => undefined,
    updateField: () => new Promise(resolve => resolve([])),
    registerField: () => {},
    getSelectedItemByFieldNameAndValue: () => ({} as ISelectItem),
    loadFromServer: () => {
    },
    validateFields: () => new Promise(resolve => resolve(true)),
    quotation: null,
    setQuotation: () => {},
    addToContextFieldNames: () => {},
    startLoadingFromServer: () => {},
    loadingAdditionalFromServer: () => {},
    getFunctionSelectItemsByFieldNameAndFormName: () => new Promise(resolve => resolve([])),
    setFields: () => {},
    nextIsAvailable: true,
    setNextIsAvailable: () => {},
    currentlyUpdatedFields: [],
    clearFirstPersonFields: () => {}
};

export const TravelQuotationCardContext = createContext(defaultContext);

const fieldsReducer = (state, action) => {
    switch (action.type) {
        case "fields":
            return {
                fields: action.payload
            };
        case "updateField": {
            if(!action.payload.fields){
                return state
            }
            const newCombinedList = state.fields.map(field =>{
                let updatedField =  Object.entries(action.payload.fields).find(item=> {
                    let fieldName = item[0].startsWith("travelPerson")? "persons-" + item[0] : item[0];
                    return fieldName === field.name && field.contextPath === action.payload.contextPath;
                });
                if (updatedField?.[0] && updatedField?.[1]) {
                    let fieldName = updatedField[0]?.startsWith("travelPerson")? "persons-" + updatedField[0] : updatedField[0];
                    let value : any = JSON.stringify(updatedField[1]);
                    let newValue = JSON.parse(value);
                    if(field.value.type === FIELD_TYPE.DATE && newValue.value !== null
                            && newValue.value !== undefined && newValue.value && newValue.isError){
                            const date = moment(newValue.value, 'DD.MM.YYYY').toDate();
                            newValue.value = moment(date).format('YYYY-MM-DD');
                        }

                    newValue.name = fieldName;
                    return {name: fieldName, value: newValue, contextPath:  action.payload.contextPath}
                }
                return field;
            })
            return {
                fields: newCombinedList
            }
        }
        default:
            throw new Error("Unsupported operation");
    }
}


const TravelQuotationCardContextProvider: React.FC = ({children}) => {
    const [registeredFields, setRegisteredFields] = useState<IResponseField[]>([]);
    const [fieldsState, fieldsDispatch] = useReducer(fieldsReducer, {
        fields: []
    })
    const {setProcessingOpen} = useContext(ProcessingContext);
    const {quotation, setQuotation} = useContext<ITravelQuotationContext>(TravelQuotationContext);
    const [nextIsAvailable, setNextIsAvailable] = useState<boolean>(true);
    const [contextFieldNames, setContextFieldNames] = useState<any[]>([]);
    const [currentlyUpdatedFields, setCurrentlyUpdatedFields] = useState<string[]>([]);
    const {errorFields, additionalErrorFields} = useContext(ErrorFieldsContext);

    useEffect(() => {
        setRegisteredFields(new Array<IResponseField>());
        setContextFieldNames(new Array<string>());
        fieldsDispatch({type: "fields", payload: []})
        }, [quotation.status]);

    const startLoadingFromServer = () => {
        loadFromServer(contextFieldNames);
    }

    const loadingAdditionalFromServer = () => {
        let contextAdditionalFields = contextFieldNames.filter(field => field.contextPath === 'mainContract.get().riskObjects.get(0)')
        loadFromServer(contextAdditionalFields, _.cloneDeep(fieldsState.fields), true);
    }

    const loadFromServer = async (fieldNames: any, startArray = [], payerIsPersonFlag?:boolean) => {
        const value = await restoreState(fieldNames, payerIsPersonFlag)
        if(!value) return
        fieldNames.forEach(field => {
            let fieldFromMeta = Object.entries(value).find(val => val[0] === field.fieldName);
            if( field.fieldName.indexOf("persons-travelPerson-") > -1 && fieldFromMeta ){
                const match = field.contextPath.match(/\d+/); 
                const index = match ? parseInt(match[0]) : null;
                if( index === null ) return
                let personFieldName = field.fieldName.split('-')[2]
                if( personFieldName === 'registrationAddress'){
                    personFieldName = 'address'
                }
                startArray.push({
                    name: fieldFromMeta[0],
                    value: {
                        ...fieldFromMeta[1],
                        value: quotation.travelWebContract.persons[index][personFieldName]
                    },
                    contextPath: field.contextPath
                })
            } else if(fieldFromMeta !== undefined){
                startArray.push({
                    name: fieldFromMeta[0],
                    value: fieldFromMeta[1],
                    contextPath: field.contextPath
                })
            }
        });
        const passportElem = startArray.find( elem => elem.name === 'persons-travelPerson-registrationDocument-number' && elem.contextPath === "mainContract.get().riskObjects.get(0)");
        if( passportElem ){
            startArray.push({
                ...passportElem,
                contextPath: 'mainContract',
                name: 'primaryInsuredParty-registrationDocument-number',
                value: {
                    ...passportElem.value,
                    name: 'primaryInsuredParty-registrationDocument-number'
                }
            })
        }
        fieldsDispatch({type: "fields", payload: startArray})
    }

    const addToContextFieldNames = (contextName: string, contextPath: string) => {
        let contextFiled = contextFieldNames;
        contextPath =  contextPath ? contextPath : 'mainContract';
        if(!contextFiled.find(field => field.fieldName === contextName && contextPath === field.contextPath)){
            contextFiled.push({fieldName: contextName,contextPath: contextPath});
            setContextFieldNames(contextFiled);
        }
    }


    const restoreState = async (fieldNames: any, payerIsPersonFlag = false): Promise<Map<string, IReactField>> => {
        try {

            const currentQuotation = payerIsPersonFlag 
                ?   {
                        ...quotation, 
                        travelWebContract: {
                            ...quotation.travelWebContract,
                            payerIsPerson: false,
                            persons: [{
                                ...quotation.travelWebContract.persons[0],
                                surname: null,
                                givenName: null,
                                mobileNumber: null,
                                birthDate: null,
                                address: null,
                            }, ...quotation.travelWebContract.persons.slice(1)]
                        }
                    }
                :   quotation
            
            return getFieldValidationMeta(fieldNames, currentQuotation , ProductType.TRAVEL);
        } catch (e) {
            throw new Error();
        }
    }

    const getFieldByName = (fieldName: string, fromForm?: boolean, contextPath?: string ) => {
        contextPath = contextPath ? contextPath : 'mainContract';
        return fieldsState.fields.find(field => field.name === fieldName && field.contextPath === contextPath);
    }

    const updateField = async (fieldName : string, value : any, context: any, contextPath: string, isProcessingOpenOnUpdate?: boolean) => {
        if (isProcessingOpenOnUpdate) {
            setProcessingOpen(true)
        }
        setNextIsAvailable(false);
        contextPath = contextPath ? contextPath : 'mainContract';
        setCurrentlyUpdatedFields(prev => ([...prev, fieldName]))
        let response = await updateFieldByRequest(context, contextPath, fieldName, value, ProductType.TRAVEL)
        setCurrentlyUpdatedFields(prev => ([...prev.filter(field => field !== fieldName)]))
        fieldsDispatch({type: "updateField", payload: {fields: response, contextPath: contextPath}})

        setNextIsAvailable(true);
        if (isProcessingOpenOnUpdate) {
            setProcessingOpen(false)
        }
    }

    const updateFields = (res: any, validationFlag = false) => {
        let arr: Array<IResponseField> = [];
        Object.entries(res).forEach(val => {
            let name = val[0].split(":")[1].startsWith("travelPerson-") ? "persons-" + val[0].split(":")[1]:
                val[0].split(":")[1];
            let field : any = val[1];
            field.name = name;
            arr.push({name: name, value: field, contextPath: val[0].split(":")[0]});
        });
        fieldsState.fields.forEach(field => {
            if (arr.find(el => el.name === field.name) == undefined) {
                arr.push(field);
            }
        })
        fieldsDispatch({type: "fields", payload: arr});
        if( !validationFlag ){
            setRegisteredFields([]);
        }
        scrollToErrorFields();
    }

    const scrollToErrorFields = () => {
        let errorElement;
        for (let fieldId of errorFields) {
            errorElement = document.querySelector(`[id*="${fieldId}"]`)
            if (errorElement) {
                errorElement.scrollIntoView();
                break;
            }
        }
        if (!errorElement) {
            for (let fieldId of additionalErrorFields) {
                let errorElement = document.querySelector(`[id*="${fieldId}"]`)
                if (errorElement) {
                    errorElement.parentElement.scrollIntoView();
                    break;
                }
            }
        }

    }


    const getSelectedItemByFieldNameAndValue = (name: string, value: any) => {
        let reactField = fieldsState.fields.find(field => field.name === name);
        if (reactField) {
            return reactField.value.selectItems.find(item => item.value === value)
        }
    }



    const validateFields = async (requestFields : FieldRequest[], context: any) => {
            try {
                const trimmedFields = requestFields.map((field) => ({
                    ...field, value: typeof field.value === 'string' ? field.value.trim() : field.value
                }))
                const trimmedQuotation = {
                    ...context,
                    travelWebContract: {
                        ...context.travelWebContract,
                        primaryInsured: {
                            ...context.travelWebContract.primaryInsured,
                            livingAddress: {
                                ...context.travelWebContract.primaryInsured.livingAddress,
                                unstructured: context.travelWebContract.primaryInsured.livingAddress.unstructured?.trim?.()
                            }
                        }
                    }
                }
                errorFields.length = 0;
                const res = await validateField(trimmedQuotation, trimmedFields, ProductType.TRAVEL);
                if (res && Object.entries(res).length) {
                    updateFields(res, true);
                    return false;
                } else {
                    scrollToErrorFields();
                }
                additionalErrorFields.length = 0;
            } catch (e) {
                return false;
            }
            return true;
    }
    const registerField = (field: IReactField, contextPath: string) => {
        if(field !== undefined){
            let arr = registeredFields;
            let index = arr.findIndex(el => el.name === field.name
                && (el.contextPath === undefined || contextPath === el.contextPath));

            if(index >= 0){
                arr.splice(index, 1);
            }

            arr.push({name: field.name,value: field, contextPath: contextPath});
           return setRegisteredFields(arr);
        }
    }
    const validateRegisteredFields = async (context: any, checkIsNotEmpty?: boolean) => {
        let values = Array.from(registeredFields.values())
        if(checkIsNotEmpty) {
            values = values.filter(field => field.value?.value)
        }
        return await validateFields(
            values
                .map(field =>{
                    let value = field.value.value;

                    if( value === null || value === ""){
                        /*
                            Ось весь цей великий костиль через підстановку значень по замовчуванню при знайденому клієнті
                            Суть в тому, що якщо підставити просто в форму, то воно його не баче...
                            І в gegisteredFields немає актуального значення
                            А зявляється лише після редагування
                            І от, для того, щоб там було значення, я і придумав оцей костиль. 
                            В квотейшні буде значення, так як я сетаю туди отримані дані в одному з юз-ефектів.
                            В context лежать нинішні значення форми
                        */
                        switch(field.name){
                            case 'primaryInsuredParty-residentCountry':{
                                value = context.travelWebContract.primaryInsured.residentCountry
                                break
                            }
                            case 'primaryInsuredParty-surname':{
                                value = context.travelWebContract.primaryInsured.surname
                                break
                            }
                            case 'primaryInsuredParty-givenName':{
                                value = context.travelWebContract.primaryInsured.givenName
                                break
                            }
                            case 'primaryInsuredParty-birthDate':{
                                value = context.travelWebContract.primaryInsured.birthDate
                                break
                            }
                            case 'primaryInsuredParty-registrationDocument-number':{
                                value = context.travelWebContract.primaryInsured?.registrationDocument?.number
                                break
                            }
                            case 'primaryInsuredParty-livingAddress-unstructured':{
                                value = context.travelWebContract.primaryInsured?.livingAddress?.unstructured
                                break
                            }
                            case 'primaryInsuredParty-mobileNumber':{
                                value = context.travelWebContract.primaryInsured.mobileNumber
                                break
                            }
                            case 'primaryInsuredParty-email':{
                                value = context.travelWebContract.primaryInsured.email
                                break
                            }
                        }
                    }
                    // Since registeredFields do not contain current value for "payerIsPerson" field,
                    // the current value is taken from the context
                    switch(field.name) {
                        case 'payerIsPerson': {
                            value = context.travelWebContract.payerIsPerson;
                            break
                        }
                    }
                    
                    if( field.name.indexOf('persons-travelPerson-') > -1 ){
                        const match = field.contextPath.match(/\d+/); 
                        const index = match ? parseInt(match[0]) : null;
                        if( index !== null ){
                            let personFieldName = field.name.split('-')[2]
                            if( personFieldName === 'registrationAddress'){
                                personFieldName = 'address'
                            }
                            if( personFieldName === 'registrationDocument'){
                                personFieldName = 'documentNumber'
                            }
                            value = context.travelWebContract.persons[index][personFieldName]
                        }
                    }
                return {fieldName: field.value.name, value: value, contextPath: field.contextPath ? field.contextPath: "mainContract" };
        }), context);
     }
     const getFunctionSelectItemsByFieldNameAndFormName = async (fieldName: string, contextPath : string, query?: string, _quotation?: object) => {
            return await getFunctionSelectItemsByDealIdAndFieldNameAndFormName(_quotation || quotation, contextPath ? contextPath :"mainContract", fieldName, ProductType.TRAVEL, query)
                .catch(error => {
                    return [];
                });
    }

    const clearFirstPersonFields = () => {
        setRegisteredFields( fields => fields.filter( field => field.contextPath !== 'mainContract.get().riskObjects.get(0)'))
    }

    return (
        <TravelQuotationCardContext.Provider value={{
            fields: fieldsState.fields,
            registeredFields,
            loadFromServer,
            getFieldByName,
            getSelectedItemByFieldNameAndValue,
            updateField,
            registerField,
            validateFields: validateRegisteredFields,
            quotation,
            setQuotation,
            startLoadingFromServer,
            addToContextFieldNames,
            getFunctionSelectItemsByFieldNameAndFormName,
            setFields: fieldsDispatch,
            loadingAdditionalFromServer,
            nextIsAvailable,
            setNextIsAvailable,
            clearFirstPersonFields,
            currentlyUpdatedFields
        }}>
            {children}
        </TravelQuotationCardContext.Provider>
    )
};

export default TravelQuotationCardContextProvider;
